import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { actions, selectors } from 'farmx-redux-core';
import { sensorApi } from 'farmx-api';
import {
  Button,
  Dropdown,
  Input,
  Menu,
  message,
  Modal,
} from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { FiInfo } from 'react-icons/fi';
import { ImSearch } from 'react-icons/im';
import StatsPage from './StatsPage';
import { ranchColumnDefs, ranchMobileColumnDefs } from '../ranchColumnDefs';
import { RanchActionCellRenderer } from './cellRendererComponents';

const RANCH_CREATE_URL = '/ranch/create/';
const RANCH_STATS_PAGE_SIZE = 50;
const LOAD_ALL_RANCHES_BATCH_SIZE = 1000;

const allColumns = [
  'ranchId',
  'name',
  'entityName',
  'entityId',
  'entityPriority',
  'subscription',
  'irrigRec',
  'satelliteImg',
  'gatewayConnected',
  'nodeConnected',
  'sensorConnected',
  'anomalyScore',
  'blockCount',
  'regionCountry',
  'location',
  'actions',
];

const softwareColumns = [
  'ranchId',
  'name',
  'entityName',
  'entityId',
  'entityPriority',
  'subscription',
  'irrigRec',
  'satelliteImg',
  'anomalyScore',
  'regionCountry',
  'actions',
];

const hardwareColumns = [
  'ranchId',
  'name',
  'entityName',
  'entityId',
  'entityPriority',
  'gatewayConnected',
  'nodeConnected',
  'sensorConnected',
  'blockCount',
  'regionCountry',
  'actions',
];

const ranchStatsPageStore = {
  page: 1,
  searchText: '',
  searchInputValue: '',
  selectedRegion: null,
  selectedRegionLabel: 'All Regions',
  selectedViewLabel: 'All',
  columnsVisible: allColumns,
  pageCache: {},
  activeQueryKey: null,
};

export default function RanchStatsPage() {
  const dispatch = useDispatch();
  const [columnsVisible, setColumnsVisible] = useState(
    ranchStatsPageStore.columnsVisible || allColumns,
  );
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(ranchStatsPageStore.page || 1);
  const pageSize = RANCH_STATS_PAGE_SIZE;
  const [searchText, setSearchText] = useState(ranchStatsPageStore.searchText || '');
  const [searchInputValue, setSearchInputValue] = useState(ranchStatsPageStore.searchInputValue || '');
  const [selectedRegion, setSelectedRegion] = useState(ranchStatsPageStore.selectedRegion || null);
  const [selectedRegionLabel, setSelectedRegionLabel] = useState(ranchStatsPageStore.selectedRegionLabel || 'All Regions');
  const [selectedViewLabel, setSelectedViewLabel] = useState(ranchStatsPageStore.selectedViewLabel || 'All');
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const [pageCache, setPageCache] = useState(ranchStatsPageStore.pageCache || {});
  const [activeQueryKey, setActiveQueryKey] = useState(ranchStatsPageStore.activeQueryKey || null);
  const pendingQueryKeyRef = useRef(null);
  const wasLoadingRef = useRef(false);
  const hasLoadedOnceRef = useRef(false);
  const loadAllRequestIdRef = useRef(0);
  const loadAllInProgressRef = useRef(false);
  const [isAllRanchesMode, setIsAllRanchesMode] = useState(false);
  const [isLoadingAllRanches, setIsLoadingAllRanches] = useState(false);
  const [allRanches, setAllRanches] = useState([]);
  const [allRanchesCount, setAllRanchesCount] = useState(0);
  const [allRanchesLastUpdated, setAllRanchesLastUpdated] = useState(null);
  const [loadAllProgress, setLoadAllProgress] = useState({ loaded: 0, total: 0 });
  const [sort] = useState([
    { colId: 'entityPriority', sort: 'desc' },
    { colId: 'ranchId', sort: 'asc' },
  ]);

  useEffect(() => {
    const onEntityIdCopied = (event) => {
      if (event?.detail?.success) {
        message.success('Copied to clipboard!');
      } else {
        message.error('Copy failed');
      }
    };

    window.addEventListener('farmx-entity-id-copied', onEntityIdCopied);
    return () => {
      window.removeEventListener('farmx-entity-id-copied', onEntityIdCopied);
    };
  }, []);

  const buildQueryParams = useCallback((targetPage = page, targetPageSize = pageSize) => {
    const query = {
      has_entity_priority: true,
      page: targetPage,
      page_size: targetPageSize,
    };
    const trimmedSearch = (searchText || '').trim();
    if (trimmedSearch) {
      query.search = trimmedSearch;
    }
    if (selectedRegion) {
      query.region = selectedRegion;
    }
    return query;
  }, [page, pageSize, searchText, selectedRegion]);

  const getQueryKey = useCallback((query) => [
    `page=${query.page || 1}`,
    `pageSize=${query.page_size || pageSize}`,
    `search=${query.search || ''}`,
    `region=${query.region || ''}`,
    `priority=${query.has_entity_priority ? 'true' : 'false'}`,
  ].join('|'), [pageSize]);

  const requestRanchStats = useCallback((query, options = {}) => {
    const { forceRefresh = false } = options;
    const queryKey = getQueryKey(query);
    setActiveQueryKey(queryKey);

    if (!forceRefresh && pageCache[queryKey]) {
      pendingQueryKeyRef.current = null;
      return;
    }

    if (forceRefresh) {
      setPageCache((currentCache) => {
        const updatedCache = { ...currentCache };
        delete updatedCache[queryKey];
        return updatedCache;
      });
    }

    pendingQueryKeyRef.current = queryKey;
    dispatch(actions.loadRanchStatsList(query));
  }, [dispatch, getQueryKey, pageCache]);

  const loadData = useCallback(() => {
    requestRanchStats(buildQueryParams(page));
  }, [buildQueryParams, page, requestRanchStats]);

  const ranchStats = useSelector((state) => selectors.selectAllRanchStats(state));
  const ranchStatsLoading = useSelector((state) => selectors.selectRanchStatsState(state));
  const {
    loading,
    lastUpdated,
    count = 0,
    next = null,
    previous = null,
  } = ranchStatsLoading;

  useEffect(() => {
    ranchStatsPageStore.page = page;
    ranchStatsPageStore.searchText = searchText;
    ranchStatsPageStore.searchInputValue = searchInputValue;
    ranchStatsPageStore.selectedRegion = selectedRegion;
    ranchStatsPageStore.selectedRegionLabel = selectedRegionLabel;
    ranchStatsPageStore.selectedViewLabel = selectedViewLabel;
    ranchStatsPageStore.columnsVisible = columnsVisible;
    ranchStatsPageStore.pageCache = pageCache;
    ranchStatsPageStore.activeQueryKey = activeQueryKey;
  }, [
    page,
    searchText,
    searchInputValue,
    selectedRegion,
    selectedRegionLabel,
    selectedViewLabel,
    columnsVisible,
    pageCache,
    activeQueryKey,
  ]);

  useEffect(() => {
    const requestJustFinished = wasLoadingRef.current && !loading;
    wasLoadingRef.current = loading;

    if (!requestJustFinished || !pendingQueryKeyRef.current) return;

    const queryKey = pendingQueryKeyRef.current;
    setPageCache((currentCache) => ({
      ...currentCache,
      [queryKey]: {
        data: ranchStats,
        count,
        next,
        previous,
        lastUpdated,
      },
    }));
    pendingQueryKeyRef.current = null;
  }, [loading, ranchStats, count, next, previous, lastUpdated]);

  useEffect(() => {
    if (!loading && lastUpdated) {
      hasLoadedOnceRef.current = true;
    }
  }, [loading, lastUpdated]);

  const activeCachedResponse = activeQueryKey ? pageCache[activeQueryKey] : null;
  const pagedData = activeCachedResponse?.data || (activeQueryKey ? [] : ranchStats);
  const pagedCount = activeCachedResponse?.count ?? count;
  const pagedLastUpdated = activeCachedResponse?.lastUpdated ?? lastUpdated;
  const pagedLoading = (loading && !activeCachedResponse)
    || (!hasLoadedOnceRef.current && !activeCachedResponse);
  const displayedData = isAllRanchesMode ? allRanches : pagedData;
  const displayedCount = isAllRanchesMode
    ? (allRanchesCount || loadAllProgress.total || pagedCount)
    : pagedCount;
  const displayedLastUpdated = isAllRanchesMode
    ? allRanchesLastUpdated
    : pagedLastUpdated;
  const displayedLoading = isAllRanchesMode ? isLoadingAllRanches : pagedLoading;
  const totalPages = Math.max(1, Math.ceil((displayedCount || 0) / pageSize));
  const hasPreviousPage = page > 1;
  const hasNextPage = page < totalPages;
  const hasTableFilters = Object.keys(filters || {}).length > 0;
  const hasSearchFilter = Boolean((searchText || '').trim() || (searchInputValue || '').trim());
  const hasRegionFilter = Boolean(selectedRegion);
  const hasViewFilter = selectedViewLabel !== 'All';
  const hasColumnChanges = columnsVisible.join('|') !== allColumns.join('|');
  const hasActiveFilters = hasTableFilters
    || hasSearchFilter
    || hasRegionFilter
    || hasViewFilter
    || hasColumnChanges;

  const leaveLoadAllMode = useCallback(() => {
    loadAllRequestIdRef.current += 1;
    loadAllInProgressRef.current = false;
    setIsLoadingAllRanches(false);
    setIsAllRanchesMode(false);
    setAllRanches([]);
    setAllRanchesCount(0);
    setAllRanchesLastUpdated(null);
    setLoadAllProgress({ loaded: 0, total: 0 });
  }, []);

  const loadAllRanches = useCallback(async (announceColumnFilter = false) => {
    if (loadAllInProgressRef.current) return;

    const requestId = loadAllRequestIdRef.current + 1;
    loadAllRequestIdRef.current = requestId;
    loadAllInProgressRef.current = true;
    setIsAllRanchesMode(true);
    setIsLoadingAllRanches(true);
    setAllRanches([]);
    setAllRanchesCount(0);
    setLoadAllProgress({ loaded: 0, total: pagedCount });
    setPage(1);

    if (announceColumnFilter) {
      message.info('Loading all ranches so this column filter includes every page.');
    }

    try {
      const fetchRanchPage = async (targetPage, ranchesById) => {
        const response = await sensorApi.fetchRanchStatsList(
          buildQueryParams(targetPage, LOAD_ALL_RANCHES_BATCH_SIZE),
        );
        if (loadAllRequestIdRef.current !== requestId) return null;

        const payload = response?.data;
        const results = Array.isArray(payload) ? payload : (payload?.results || []);
        const totalCount = Array.isArray(payload)
          ? results.length
          : (payload?.count ?? results.length);

        results.forEach((ranch) => {
          if (ranch?.id !== undefined && ranch?.id !== null) {
            ranchesById.set(ranch.id, ranch);
          }
        });

        setLoadAllProgress({
          loaded: ranchesById.size,
          total: totalCount,
        });

        if (!Array.isArray(payload) && payload?.next) {
          return fetchRanchPage(targetPage + 1, ranchesById);
        }
        return ranchesById;
      };

      const ranchesById = await fetchRanchPage(1, new Map());
      if (!ranchesById) return;

      const loadedRanches = Array.from(ranchesById.values());
      setAllRanches(loadedRanches);
      setAllRanchesCount(loadedRanches.length);
      setAllRanchesLastUpdated(Date.now());
    } catch (error) {
      if (loadAllRequestIdRef.current !== requestId) return;
      setIsAllRanchesMode(false);
      setAllRanches([]);
      setAllRanchesCount(0);
      message.error('Unable to load all ranches. Please try again.');
    } finally {
      if (loadAllRequestIdRef.current === requestId) {
        loadAllInProgressRef.current = false;
        setIsLoadingAllRanches(false);
      }
    }
  }, [buildQueryParams, pagedCount]);

  const returnToPagedView = useCallback(() => {
    leaveLoadAllMode();
    setFilters({});
    setPage(1);
    requestRanchStats(buildQueryParams(1));
  }, [buildQueryParams, leaveLoadAllMode, requestRanchStats]);

  const refreshData = useCallback(() => {
    if (isAllRanchesMode) {
      loadAllRanches();
      return;
    }
    requestRanchStats(buildQueryParams(page), { forceRefresh: true });
  }, [
    buildQueryParams,
    isAllRanchesMode,
    loadAllRanches,
    page,
    requestRanchStats,
  ]);

  const handleTableFiltersChanged = useCallback((filterModel) => {
    const nextFilters = filterModel || {};
    setFilters(nextFilters);

    if (Object.keys(nextFilters).length > 0 && !isAllRanchesMode) {
      loadAllRanches(true);
    }
  }, [isAllRanchesMode, loadAllRanches]);

  useEffect(() => () => {
    loadAllRequestIdRef.current += 1;
    loadAllInProgressRef.current = false;
  }, []);

  const goToPage = useCallback((nextPage) => {
    const targetPage = Math.min(Math.max(1, nextPage), totalPages);
    setPage(targetPage);
    requestRanchStats(buildQueryParams(targetPage));
  }, [buildQueryParams, requestRanchStats, totalPages]);

  const setRegionFilter = (region, label) => {
    leaveLoadAllMode();
    setSelectedRegion(region);
    setPage(1);
    setSelectedRegionLabel(label);
    const query = {
      has_entity_priority: true,
      page: 1,
      page_size: pageSize,
    };
    const trimmedSearch = (searchText || '').trim();
    if (trimmedSearch) {
      query.search = trimmedSearch;
    }
    query.region = region;
    requestRanchStats(query);
  };

  const clearFilters = () => {
    leaveLoadAllMode();
    setFilters({});
    setColumnsVisible(allColumns);
    setSearchText('');
    setSearchInputValue('');
    setSelectedRegion(null);
    setPage(1);
    setSelectedRegionLabel('All Regions');
    setSelectedViewLabel('All');
    requestRanchStats({
      has_entity_priority: true,
      page: 1,
      page_size: pageSize,
    });
  };

  const submitSearch = useCallback((rawValue = searchInputValue) => {
    const value = rawValue || '';
    const trimmedValue = value.trim();

    leaveLoadAllMode();
    setSearchInputValue(value);
    setSearchText(value);
    setPage(1);

    const query = {
      has_entity_priority: true,
      page: 1,
      page_size: pageSize,
    };
    if (trimmedValue) {
      query.search = trimmedValue;
    }
    if (selectedRegion) {
      query.region = selectedRegion;
    }
    requestRanchStats(query);
  }, [
    leaveLoadAllMode,
    pageSize,
    requestRanchStats,
    searchInputValue,
    selectedRegion,
  ]);

  const onGlobalSearchChange = (event) => {
    const value = event?.target?.value || '';
    setSearchInputValue(value);

    if (value === '' && searchText) {
      submitSearch('');
    }
  };

  const regionPresetMenu = (
    <Menu>
      <Menu.Item key="region-usa" onClick={() => setRegionFilter('USA', 'USA')}>USA</Menu.Item>
      <Menu.Item key="region-india" onClick={() => setRegionFilter('India', 'India')}>India</Menu.Item>
      <Menu.Item key="region-japan" onClick={() => setRegionFilter('Japan', 'Japan')}>Japan</Menu.Item>
      <Menu.Item
        key="region-clear"
        onClick={() => {
          leaveLoadAllMode();
          setSelectedRegion(null);
          setPage(1);
          setSelectedRegionLabel('All Regions');
          const query = {
            has_entity_priority: true,
            page: 1,
            page_size: pageSize,
          };
          const trimmedSearch = (searchText || '').trim();
          if (trimmedSearch) {
            query.search = trimmedSearch;
          }
          requestRanchStats(query);
        }}
      >
        All Regions
      </Menu.Item>
    </Menu>
  );

  const viewPresetMenu = (
    <Menu>
      <Menu.Item
        key="view-all"
        onClick={() => {
          setColumnsVisible(allColumns);
          setPage(1);
          setSelectedViewLabel('All');
        }}
      >
        All
      </Menu.Item>
      <Menu.Item
        key="view-software"
        onClick={() => {
          setColumnsVisible(softwareColumns);
          setPage(1);
          setSelectedViewLabel('Software Services');
        }}
      >
        Software Services
      </Menu.Item>
      <Menu.Item
        key="view-hardware"
        onClick={() => {
          setColumnsVisible(hardwareColumns);
          setPage(1);
          setSelectedViewLabel('Hardware Status');
        }}
      >
        Hardware Status
      </Menu.Item>
    </Menu>
  );

  const fullscreenPaginationControls = isAllRanchesMode ? (
    <Button disabled>
      All
      {' '}
      {displayedCount}
      {' '}
      ranches loaded
    </Button>
  ) : (
    <>
      <Button
        onClick={() => goToPage(Math.max(1, page - 1))}
        disabled={!hasPreviousPage || displayedLoading}
      >
        <FaChevronLeft />
      </Button>
      <Button disabled>
        {page}
        {' / '}
        {totalPages}
      </Button>
      <Button
        onClick={() => goToPage(page + 1)}
        disabled={!hasNextPage || displayedLoading}
      >
        <FaChevronRight />
      </Button>
      <Button disabled>
        Total ranches:
        {' '}
        {displayedCount}
      </Button>
    </>
  );

  let loadAllButtonLabel = `Load all ${pagedCount} ranches`;
  if (isAllRanchesMode) loadAllButtonLabel = 'Use paged view';
  if (isLoadingAllRanches) {
    loadAllButtonLabel = `Loading ${loadAllProgress.loaded} / ${loadAllProgress.total || pagedCount}`;
  }

  return (
    <StatsPage
      title="Ranch Statistics"
      loadData={loadData}
      refreshData={refreshData}
      data={displayedData}
      loading={displayedLoading}
      lastUpdated={displayedLastUpdated}
      rowCount={displayedCount}
      tableClassName="ranch-stats-grid"
      enhancedInteractions
      fullscreenControls={fullscreenPaginationControls}
      extra={[
        <Button
          key="legend"
          onClick={() => setIsLegendOpen(true)}
          aria-label="Open ranch status legend"
          className="ranch-legend-trigger-button"
          title="Helper"
        >
          <FiInfo />
        </Button>,
        <Button
          key="load-all-ranches"
          onClick={isAllRanchesMode
            ? returnToPagedView
            : () => loadAllRanches()}
          loading={isLoadingAllRanches}
          disabled={!isAllRanchesMode && (pagedLoading || pagedCount <= 0)}
          title={isAllRanchesMode
            ? 'Return to the 50-ranch paginated view'
            : 'Load every ranch in the current result set'}
        >
          {loadAllButtonLabel}
        </Button>,
        <Button
          key="create-ranch"
          type="primary"
          onClick={() => window.location.assign(RANCH_CREATE_URL)}
        >
          Create Ranch
        </Button>,
        <Button
          key="mobile-clear"
          type={hasActiveFilters ? 'primary' : 'default'}
          onClick={clearFilters}
          className="ranch-mobile-top-clear-button"
          disabled={!hasActiveFilters}
        >
          <FaTimes />
          <span>Clear</span>
        </Button>,
      ]}
      sort={sort}
      filters={filters}
      filtersChangedCallback={handleTableFiltersChanged}
      columnsVisible={columnsVisible}
      frameworkComponents={{
        ranchActionsMenu: RanchActionCellRenderer,
      }}
      columnDefinitionsOverride={ranchColumnDefs}
      mobileColumnDefinitionsOverride={ranchMobileColumnDefs}
      columns={allColumns}
      headerContent={(
        <>
          <span className="header-btns ranch-stats-toolbar">
            <Button
              type={hasActiveFilters ? 'primary' : 'default'}
              onClick={clearFilters}
              disabled={!hasActiveFilters}
            >
              <FaTimes />
              <span>Clear</span>
            </Button>
            <Dropdown overlay={regionPresetMenu}>
              <Button className="ranch-toolbar-filter ranch-toolbar-filter-region">
                Region:
                {' '}
                {selectedRegionLabel}
                {' '}
                <DownOutlined />
              </Button>
            </Dropdown>
            <Dropdown overlay={viewPresetMenu}>
              <Button className="ranch-toolbar-filter ranch-toolbar-filter-view">
                View:
                {' '}
                {selectedViewLabel}
                {' '}
                <DownOutlined />
              </Button>
            </Dropdown>
            <span className="ranch-toolbar-actions">
              <Input
                className="ranch-search-input"
                placeholder="Search ranches or entities"
                value={searchInputValue}
                onChange={onGlobalSearchChange}
                onPressEnter={() => submitSearch(searchInputValue)}
                suffix={(
                  <Button
                    type="link"
                    size="small"
                    className="ranch-search-submit-button"
                    onClick={() => submitSearch(searchInputValue)}
                    aria-label="Search ranch statistics"
                  >
                    <ImSearch className="ranch-search-icon" />
                  </Button>
                )}
                allowClear
              />
              {isAllRanchesMode ? (
                <Button disabled>
                  All
                  {' '}
                  {displayedCount}
                  {' '}
                  ranches loaded
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => goToPage(Math.max(1, page - 1))}
                    disabled={!hasPreviousPage || displayedLoading}
                    title="Previous page"
                    aria-label="Previous ranch statistics page"
                  >
                    <FaChevronLeft />
                  </Button>
                  <Button disabled>
                    {page}
                    {' / '}
                    {totalPages}
                  </Button>
                  <Button
                    onClick={() => goToPage(page + 1)}
                    disabled={!hasNextPage || displayedLoading}
                    title="Next page"
                    aria-label="Next ranch statistics page"
                  >
                    <FaChevronRight />
                  </Button>
                </>
              )}
              <Button disabled>
                Total ranches:
                {' '}
                {displayedCount}
              </Button>
            </span>
          </span>
          <Modal
            open={isLegendOpen}
            onCancel={() => setIsLegendOpen(false)}
            footer={null}
            title="Ranch Status Legend"
            centered
            className="ranch-legend-modal"
          >
            <p className="ranch-legend-description">
              These colors explain ranch health, service status, and anomaly severity in the table.
            </p>
            <div className="ranch-legend-section">
              <div className="ranch-legend-section-title">Hardware / Service Status</div>
              <div className="ranch-legend-grid">
                <div className="ranch-legend-item">
                  <span className="ranch-legend-swatch ranch-legend-swatch-green" />
                  <span>Healthy</span>
                </div>
                <div className="ranch-legend-item">
                  <span className="ranch-legend-swatch ranch-legend-swatch-yellow" />
                  <span>Warning</span>
                </div>
                <div className="ranch-legend-item">
                  <span className="ranch-legend-swatch ranch-legend-swatch-red" />
                  <span>Issue</span>
                </div>
                <div className="ranch-legend-item">
                  <span className="ranch-legend-swatch ranch-legend-swatch-empty" />
                  <span>No Data</span>
                </div>
              </div>
            </div>
            <div className="ranch-legend-section">
              <div className="ranch-legend-section-title">Anomaly Score</div>
              <div className="ranch-legend-grid">
                <div className="ranch-legend-item">
                  <span className="ranch-legend-swatch ranch-legend-swatch-blue" />
                  <span>Normal</span>
                </div>
                <div className="ranch-legend-item">
                  <span className="ranch-legend-swatch ranch-legend-swatch-yellow" />
                  <span>Attention</span>
                </div>
                <div className="ranch-legend-item">
                  <span className="ranch-legend-swatch ranch-legend-swatch-red" />
                  <span>High Risk</span>
                </div>
              </div>
            </div>
          </Modal>
        </>
      )}
    />
  );
}
